/**
 * The Knowledge panel's end state, carried from the live call into the
 * after-call incident report (owner report 2026-09-23: "The knowledge didn't
 * or doesn't always save its state into the after call incident report view").
 *
 * RelatedDocumentsPanel snapshots its documents and overall summary into
 * `aiAnalysis.relatedDocumentsSnapshot` / `relatedDocumentsOverallSummary`, and
 * the report hydrates them from `ai_analysis.full_state` — but every writer of
 * full_state (the end-call archive and the live sync) builds an explicit field
 * list that left both out, so they were never saved. What the report showed was
 * a fresh post-call re-query, which is why it worked only "sometimes".
 *
 * These keys travel as a pair: the summary describes that document list.
 */

type JsonRecord = Record<string, unknown>

export interface KnowledgePersistenceState {
  relatedDocumentsSnapshot: Array<Record<string, unknown>>
  relatedDocumentsOverallSummary: string | null
}

export function selectKnowledgeStateForPersistence(
  analysis: Partial<KnowledgePersistenceState> | null | undefined,
): KnowledgePersistenceState {
  return {
    relatedDocumentsSnapshot: Array.isArray(analysis?.relatedDocumentsSnapshot)
      ? analysis!.relatedDocumentsSnapshot
      : [],
    relatedDocumentsOverallSummary:
      typeof analysis?.relatedDocumentsOverallSummary === 'string'
        ? analysis.relatedDocumentsOverallSummary
        : null,
  }
}

/**
 * A writer with no documents (a report page that never received the live
 * snapshot, a live sync before the panel ever ran) must not erase a saved
 * snapshot. The pair is restored together, so a summary never outlives or
 * loses its documents.
 */
export function preserveSavedKnowledgeState(
  target: JsonRecord,
  existing: JsonRecord | null | undefined,
): void {
  const incoming = target.relatedDocumentsSnapshot
  if (Array.isArray(incoming) && incoming.length > 0) return
  const saved = existing?.relatedDocumentsSnapshot
  if (!Array.isArray(saved) || saved.length === 0) return
  target.relatedDocumentsSnapshot = saved
  target.relatedDocumentsOverallSummary =
    typeof existing?.relatedDocumentsOverallSummary === 'string'
      ? existing.relatedDocumentsOverallSummary
      : null
}
